import type { ModelDoc, ModelMaterial } from '../../lib/model-doc.js';
import { resolvePartPath, setMaterial, setPartField } from '../../lib/model-doc.js';
import { promptText } from '../../lib/prompt-dialog.js';
import type { IconName } from '../ui/icons.js';

/**
 * Starting points for model materials (Materials → New material ▾ and the
 * part Material field). Values are tuned for the engine's standard / toon /
 * unlit shaders; every one stays fully editable afterwards.
 */
export interface MaterialPreset {
  id: string;
  label: string;
  hint: string;
  icon: IconName;
  material: ModelMaterial;
}

export const MATERIAL_PRESETS: readonly MaterialPreset[] = [
  { id: 'blank', label: 'Blank', hint: 'Light gray, matte', icon: 'material', material: { color: [0.8, 0.8, 0.8, 1], roughness: 0.8 } },
  {
    id: 'paint',
    label: 'Car paint',
    hint: 'Glossy metallic paint (tint it per instance with mesh.materials / set_slot_material)',
    icon: 'material',
    material: { shader: 'standard', color: [0.85, 0.1, 0.55, 1], metallic: 0.55, roughness: 0.22 },
  },
  {
    id: 'anime-paint',
    label: 'Anime paint (toon)',
    hint: 'Cel-shaded paint with a bright rim light',
    icon: 'material',
    material: {
      shader: 'toon',
      color: [0.95, 0.15, 0.6, 1],
      metallic: 0.2,
      roughness: 0.35,
      toonSettings: { bandWeight: 0.25, shadowColor: [0.35, 0.2, 0.5], rimStrength: 0.9, rimColor: [1, 0.85, 1], specularStrength: 0.8 },
    },
  },
  { id: 'glass', label: 'Tinted glass', hint: 'Dark mirror-like canopy glass', icon: 'material', material: { shader: 'standard', color: [0.04, 0.06, 0.12, 1], metallic: 0.9, roughness: 0.05 } },
  { id: 'rubber', label: 'Rubber', hint: 'Tyres, seals', icon: 'material', material: { shader: 'standard', color: [0.05, 0.05, 0.06, 1], metallic: 0, roughness: 0.92 } },
  { id: 'chrome', label: 'Chrome', hint: 'Polished metal: rims, exhausts, trim', icon: 'material', material: { shader: 'standard', color: [0.9, 0.9, 0.95, 1], metallic: 1, roughness: 0.08 } },
  { id: 'carbon', label: 'Carbon / dark plastic', hint: 'Spoilers, diffusers, splitters', icon: 'material', material: { shader: 'standard', color: [0.07, 0.07, 0.09, 1], metallic: 0.3, roughness: 0.4 } },
  { id: 'matte', label: 'Matte plastic', hint: 'Interior, bumpers', icon: 'material', material: { shader: 'standard', color: [0.2, 0.2, 0.22, 1], metallic: 0, roughness: 0.75 } },
  { id: 'neon-cyan', label: 'Neon (cyan)', hint: 'Unlit glow that blooms', icon: 'sparkles', material: { shader: 'unlit', color: [0, 0.9, 1, 1], emissive: [0, 0.9, 1], emissiveIntensity: 4 } },
  { id: 'neon-pink', label: 'Neon (pink)', hint: 'Unlit glow that blooms', icon: 'sparkles', material: { shader: 'unlit', color: [1, 0.12, 0.7, 1], emissive: [1, 0.12, 0.7], emissiveIntensity: 4 } },
  { id: 'headlight', label: 'Headlight', hint: 'Bright white-blue lamp', icon: 'sparkles', material: { shader: 'unlit', color: [0.85, 0.95, 1, 1], emissive: [0.85, 0.95, 1], emissiveIntensity: 6 } },
  { id: 'brake', label: 'Brake light', hint: 'Red lamp', icon: 'sparkles', material: { shader: 'unlit', color: [1, 0.05, 0.15, 1], emissive: [1, 0.05, 0.15], emissiveIntensity: 5 } },
];

function uniqueName(doc: ModelDoc, base: string): string {
  const clean = base.trim().replace(/\s+/g, '_').toLowerCase() || 'material';
  if (!doc.materials?.[clean]) return clean;
  let n = 2;
  while (doc.materials[`${clean}${n}`]) n += 1;
  return `${clean}${n}`;
}

function assign(doc: ModelDoc, parts: readonly string[], name: string): ModelDoc {
  let next = doc;
  for (const p of parts) {
    const path = resolvePartPath(next, p);
    if (path) next = setPartField(next, path, 'material', name === 'default' ? undefined : name);
  }
  return next;
}

/**
 * Asks for a name, adds the preset as a new material and assigns it to
 * `assignTo` (part paths). Returns the new doc, or null when cancelled.
 */
export async function createPresetMaterial(doc: ModelDoc, preset: MaterialPreset, assignTo: readonly string[]): Promise<ModelDoc | null> {
  const suggested = uniqueName(doc, preset.id === 'blank' ? 'material' : preset.id.replace(/-/g, '_'));
  const name = (await promptText(assignTo.length ? `New ${preset.label} material (assigned to ${assignTo.length} part${assignTo.length > 1 ? 's' : ''})` : `New ${preset.label} material`, suggested))?.trim();
  if (!name) return null;
  const final = doc.materials?.[name] ? uniqueName(doc, name) : name;
  const material = structuredClone(preset.material);
  return assign(setMaterial(doc, final, material), assignTo, final);
}
